import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://amrutha-kalasam-backend.greenfield-51cf4966.centralindia.azurecontainerapps.io/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  duration_minutes: number;
}

export interface Slot {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  is_booked: boolean;
  service_name?: string;
  price?: number;
}

export interface UserDetails {
  name: string;
  email: string;
  phone: string;
}

export interface BookingData {
  slot_id: string;
  user_details: UserDetails;
  service_id: string;
  consultation_mode: 'online' | 'offline';
}

export interface PaymentDetails {
  order_id: string;
  amount: number;
  currency: string;
  key_id: string;
}

export interface BookingResponse {
  message: string;
  payment_details: PaymentDetails;
  booking_details: {
    slot_id: string;
    service_name: string;
    date: string;
    time: string;
  };
}

export interface PaymentVerification {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface PaymentCancelRequest {
  razorpay_order_id: string;
}

// API functions
export const servicesApi = {
  getAll: (): Promise<Service[]> =>
    apiClient.get('/services').then(response => response.data),
};

export const slotsApi = {
  getByDate: (date?: string): Promise<Slot[]> =>
    apiClient.get('/slots', { params: date ? { date } : {} }).then(response => response.data),
};

export const bookingApi = {
  bookSlot: (bookingData: BookingData): Promise<BookingResponse> =>
    apiClient.post('/book-slot', bookingData).then(response => response.data),
  
  verifyPayment: (paymentData: PaymentVerification): Promise<{ message: string }> =>
    apiClient.post('/verify-payment', paymentData).then(response => response.data),

  cancelPayment: (data: PaymentCancelRequest): Promise<{ message: string; status: string }> =>
    apiClient.post('/cancel-payment', data).then(r => r.data),
};

export interface KundaliRequest {
  name: string;
  date_of_birth: string;
  time_of_birth: string;
  place_of_birth: string;
}

export interface PlanetData {
  name: string;
  name_telugu: string;
  sign: string;
  sign_telugu: string;
  sign_sanskrit: string;
  sign_index: number;
  degree: number;
  total_degree: number;
  nakshatra: string;
  nakshatra_telugu: string;
  nakshatra_lord: string;
  nakshatra_lord_telugu: string;
  nakshatra_pada: number;
  house: number;
  is_retrograde: boolean;
  speed_longitude: number;
}

export interface AntardashaPeriod {
  planet: string;
  planet_telugu: string;
  start_date: string;
  end_date: string;
  duration_years: number;
  is_current: boolean;
}

export interface DivisionalChartEntry {
  name: string;
  name_telugu: string;
  sign: string;
  sign_telugu: string;
  sign_index: number;
}

export interface DivisionalChart {
  name: string;
  name_telugu: string;
  planets: DivisionalChartEntry[];
}

export interface ShabdalaEntry {
  sthana_bala: number;
  dig_bala: number;
  naisargika_bala: number;
  chesta_bala: number;
  drik_bala: number;
  kala_bala: number;
  total: number;
  required_minimum: number | null;
  is_strong: boolean | null;
}

export interface KundaliData {
  birth_details: {
    name: string;
    date_of_birth: string;
    time_of_birth: string;
    place_of_birth: string;
    latitude: number;
    longitude: number;
    timezone: string;
    utc_datetime: string;
  };
  ascendant: {
    sign: string;
    sign_telugu: string;
    sign_sanskrit: string;
    degree: number;
    sign_index: number;
  };
  planets: PlanetData[];
  houses: {
    number: number;
    sign: string;
    sign_sanskrit: string;
    sign_index: number;
    planets: string[];
  }[];
  dasha: {
    current_mahadasha: string;
    current_antardasha: string;
    current_mahadasha_end: string;
    periods: {
      planet: string;
      planet_telugu: string;
      start_date: string;
      end_date: string;
      duration_years: number;
      is_current: boolean;
      antardasha: AntardashaPeriod[];
    }[];
  };
  divisional_charts: Record<string, DivisionalChart>;
  shadbala: {
    planets: Record<string, ShabdalaEntry>;
    note: string;
  };
}

export interface SavedChart {
  _id: string;
  name: string;
  date_of_birth: string;
  time_of_birth: string;
  place_of_birth: string;
  created_at: string;
}

export interface AiReportRequest {
  name: string;
  date_of_birth: string;
  time_of_birth: string;
  place_of_birth: string;
  focus_areas: string[];
  charts: string[];
}

export interface AiPredictionReport {
  name: string;
  generated_at: string;
  report: string;
  focus_areas: string[];
  charts_used: string[];
}

export interface AiReportPdfRequest {
  name: string;
  date_of_birth: string;
  time_of_birth: string;
  place_of_birth: string;
  report: string;
  focus_areas: string[];
  charts_used: string[];
}

export const kundaliApi = {
  generate: (data: KundaliRequest): Promise<KundaliData> =>
    apiClient.post('/kundali/generate', data).then(r => r.data),
  
  downloadReport: async (data: KundaliRequest): Promise<void> => {
    const response = await apiClient.post('/kundali/report', data, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `kundali_${data.name.replace(/\s+/g, '_')}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  getSavedCharts: (): Promise<SavedChart[]> =>
    apiClient.get('/kundali/saved-charts').then(r => r.data),

  saveChart: (data: KundaliRequest): Promise<SavedChart> =>
    apiClient.post('/kundali/saved-charts', data).then(r => r.data),

  deleteSavedChart: (chartId: string): Promise<void> =>
    apiClient.delete(`/kundali/saved-charts/${chartId}`).then(() => {}),

  generateAiReport: (data: AiReportRequest): Promise<AiPredictionReport> =>
    apiClient.post('/admin/kundali/ai-report', data).then(r => r.data),

  downloadAiReportPdf: async (data: AiReportPdfRequest): Promise<void> => {
    const response = await apiClient.post('/admin/kundali/ai-report/pdf', data, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ai_prediction_report_${data.name.replace(/\s+/g, '_')}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

export interface MatchingRequest {
  boy_name: string;
  boy_dob: string;
  boy_tob: string;
  boy_pob: string;
  girl_name: string;
  girl_dob: string;
  girl_tob: string;
  girl_pob: string;
}

export interface KootaScore {
  name: string;
  nameTelugu: string;
  maxScore: number;
  obtained: number;
  description: string;
}

export interface PersonInfo {
  name: string;
  rashi: string;
  rashiTelugu: string;
  nakshatra: string;
  nakshatraPada: number;
  moonDegree: number;
}

export interface MatchingResult {
  boy: PersonInfo;
  girl: PersonInfo;
  scores: KootaScore[];
  totalScore: number;
  maxScore: number;
  percentage: number;
  verdict: {
    level: string;
    levelTelugu: string;
    message: string;
    color: string;
  };
  disclaimer: string;
}

export const matchingApi = {
  calculate: (data: MatchingRequest): Promise<MatchingResult> =>
    apiClient.post('/kundali/matching', data).then(r => r.data),
};

export interface RashiPrediction {
  career: string;
  love: string;
  health: string;
  finance: string;
  luckyNumber: number;
  luckyColor: string;
}

export interface RashiInfo {
  id: number;
  name: string;
  nameTelugu: string;
  symbol: string;
  element: string;
  rulingPlanet: string;
  rulingPlanetTelugu: string;
  dateRange: string;
  prediction: RashiPrediction;
}

export interface TransitInfo {
  planet: string;
  sign: string;
  degree: number;
}

export interface RashiPhalResponse {
  lastUpdated: string;
  rashis: RashiInfo[];
  transits: TransitInfo[];
  source: string;
}

export const rashiPhalApi = {
  getToday: (): Promise<RashiPhalResponse> =>
    apiClient.get('/rashi-phal').then(r => r.data),
};

export default apiClient;
